import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, Crown, User, Bot } from "lucide-react";
import { formatTime } from "@/lib/clock-utils";
import { AvatarDisplay } from "./AvatarDisplay";
import type { AvatarConfig } from "@/lib/avatar-config";

interface PlayerInfoCardProps {
  name: string;
  isBot?: boolean;
  elo?: number;
  color: 'white' | 'black';
  isActivePlayer: boolean;
  timeMs: number;
  isCurrentUser?: boolean;
  isConnected?: boolean;
  compact?: boolean;
  avatarConfig?: AvatarConfig | null;
  avatarUrl?: string | null;
}

export function PlayerInfoCard({
  name,
  isBot = false,
  elo,
  color,
  isActivePlayer,
  timeMs,
  isCurrentUser = false,
  isConnected = true,
  compact = false,
  avatarConfig,
  avatarUrl
}: PlayerInfoCardProps) {
  const isLowTime = timeMs < 30000;
  const isCriticalTime = timeMs < 10000;

  if (compact) {
    return (
      <Card className={`
        transition-all duration-200 border
        ${isActivePlayer ? 'border-primary bg-primary/5' : 'border-border'}
        ${!isConnected ? 'opacity-75' : ''}
      `}>
        <CardContent className="p-3">
          <div className="flex items-center gap-2">
            {/* Avatar */}
            <AvatarDisplay
              username={name}
              avatarConfig={avatarConfig}
              avatarUrl={avatarUrl}
              size="sm"
              showAnimation={isActivePlayer && !isBot}
              animationType={isActivePlayer ? "idle" : undefined}
              className="border border-border"
            />
            
            {/* Player Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1 mb-1">
                <div className={`w-2 h-2 rounded-full ${
                  color === 'white' ? 'bg-background border border-foreground' : 'bg-foreground'
                }`} />
                <h3 className="font-medium text-sm truncate">{name}</h3>
                {isCurrentUser && (
                  <Badge variant="outline" className="text-xs px-1 py-0">You</Badge>
                )}
              </div>
              
              <div className="flex items-center gap-1">
                {isBot && elo && (
                  <Badge variant="secondary" className="text-xs px-1 py-0">
                    {elo}
                  </Badge>
                )}
                {isActivePlayer && (
                  <Badge variant="default" className="text-xs px-1 py-0 bg-primary/20 text-primary border-primary/30">
                    <Crown className="h-2 w-2 mr-1" />
                    Turn
                  </Badge>
                )}
              </div>
            </div>

            {/* Compact Time Display */}
            <div className={`
              flex items-center gap-1 px-2 py-1 rounded font-mono text-sm font-bold
              transition-all duration-200
              ${isActivePlayer ? 'bg-primary/10 text-primary' : 'bg-muted/50 text-muted-foreground'}
              ${isLowTime ? 'animate-pulse' : ''}
              ${isCriticalTime ? 'bg-destructive/10 text-destructive' : ''}
            `}>
              <Clock className={`h-3 w-3 ${isCriticalTime ? 'text-destructive' : isActivePlayer ? 'text-primary' : 'text-muted-foreground'}`} />
              <span className={`text-xs ${isCriticalTime ? 'text-destructive' : isLowTime ? 'text-orange-500' : ''}`}>
                {formatTime(timeMs)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`
      transition-all duration-300 border-2
      ${isActivePlayer ? 'border-primary shadow-lg shadow-primary/20 bg-primary/5 scale-[1.02] ring-1 ring-primary/30' : 'border-border hover:shadow-md'}
      ${!isConnected ? 'opacity-75' : ''}
    `}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          {/* Player Info */}
          <div className="flex items-center gap-3">
            <AvatarDisplay
              username={name}
              avatarConfig={avatarConfig}
              avatarUrl={avatarUrl}
              size="lg"
              showAnimation={isActivePlayer && !isBot}
              animationType={isActivePlayer ? "celebration" : "idle"}
              className="border-2 border-border shadow-sm"
            />
            
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-base">{name}</h3>
                {isCurrentUser && (
                  <Badge variant="outline" className="text-xs">You</Badge>
                )}
                {isBot && elo && (
                  <Badge variant="secondary" className="text-xs">
                    {elo} ELO
                  </Badge>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${
                  color === 'white' ? 'bg-background border-2 border-foreground' : 'bg-foreground'
                }`} />
                <span className="text-sm text-muted-foreground capitalize">
                  {color}
                </span>
                {isActivePlayer && (
                  <Badge variant="default" className="text-xs bg-primary/25 text-primary border-primary/50 shadow-sm animate-pulse">
                    <Crown className="h-3 w-3 mr-1" />
                    Your Turn
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Time Display */}
          <div className={`
            flex items-center gap-2 px-4 py-3 rounded-lg font-mono text-xl font-bold
            transition-all duration-300 shadow-sm
            ${isActivePlayer ? 'bg-primary/15 text-primary border-2 border-primary/30 shadow-primary/20' : 'bg-muted/50 text-muted-foreground border border-border'}
            ${isLowTime ? 'animate-pulse' : ''}
            ${isCriticalTime ? 'bg-destructive/15 text-destructive border-2 border-destructive/40 shadow-destructive/20' : ''}
          `}>
            <Clock className={`h-4 w-4 ${isCriticalTime ? 'text-destructive' : isActivePlayer ? 'text-primary' : 'text-muted-foreground'}`} />
            <span className={`
              ${isCriticalTime ? 'text-destructive' : isLowTime ? 'text-orange-500' : ''}
            `}>
              {formatTime(timeMs)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}